import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FilterListIcon from '@mui/icons-material/FilterList';
import FunctionsIcon from '@mui/icons-material/Functions';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import type { SvgIconProps } from '@mui/material';
import type { ComponentType } from 'react';

import type {
  ProjectArtifactFilterKind,
  ProjectArtifactKind,
  ProjectArtifactScopeFilter,
} from '../types';

/**
 * Canonical per-type icon, shared by the filter chips and the Name-column
 * row icon so a type reads identically in both places (OR-6123 F9 design):
 *
 * - cohort            → groups (same glyph as the cohort header's Type item)
 * - analytical_dataset → table  (a tabular result set)
 * - variable          → Σ       (an analytical variable / function)
 * - concept_group     → {}      (a set of codes)
 * - analysis          → table  (reuses the analytical-dataset glyph — analysis
 *   is also a formatted-table result set; revisit once it has its own).
 */
export const ARTIFACT_KIND_ICON: Record<ProjectArtifactKind, ComponentType<SvgIconProps>> = {
  cohort: GroupsOutlinedIcon,
  analytical_dataset: TableChartOutlinedIcon,
  variable: FunctionsIcon,
  concept_group: DataObjectIcon,
  analysis: AnalyticsOutlinedIcon,
};

/**
 * Filter-chip icon set — the per-kind icons plus the `all` funnel. Keyed by
 * {@link ProjectArtifactFilterKind} so the toolbar can index it directly.
 */
export const ARTIFACT_FILTER_ICON: Record<
  ProjectArtifactFilterKind,
  ComponentType<SvgIconProps>
> = {
  all: FilterListIcon,
  ...ARTIFACT_KIND_ICON,
};

/**
 * Scope-facet chip icon set (OR-6576): the same leading-icon treatment as
 * the type filter chips, applied to the All scopes / Project / Organization
 * facet row. `organization` reuses {@link CorporateFareIcon}, the same glyph
 * the data grid uses to mark organization-scoped rows, so a scope reads
 * identically in both places.
 */
export const ARTIFACT_SCOPE_ICON: Record<
  ProjectArtifactScopeFilter,
  ComponentType<SvgIconProps>
> = {
  all: FilterListIcon,
  project: GroupWorkOutlinedIcon,
  organization: CorporateFareIcon,
};
